import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Video, Webhook, Send, BarChart3, AlertCircle, CheckCircle2, XCircle, Clock,
  Languages, Trophy, TrendingUp, Users, Euro, Save, Trash2, PlayCircle, ChevronRight, Sparkles,
  Upload, Film, ExternalLink, Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGUAGES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English',  flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch',  flag: '🇩🇪' },
  { code: 'es', label: 'Español',  flag: '🇪🇸' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', label: 'Polski',   flag: '🇵🇱' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

const VARIANT_INFO = {
  A: { label: 'Consecutiva', desc: 'CI+0 · CI+1 · CI+2', color: 'bg-rose-50 border-rose-200 text-rose-900' },
  B: { label: 'Distribuita', desc: 'CI+0 · CI+7 · CI+14', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
  C: { label: 'Ogni 3 giorni', desc: 'CI+0 · CI+3 · CI+6', color: 'bg-blue-50 border-blue-200 text-blue-900' },
};

export default function GuestVideoCampaigns() {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState({});
  const [videos, setVideos] = useState({});
  const [sentItems, setSentItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [abComparison, setAbComparison] = useState(null);
  const [byLang, setByLang] = useState(null);
  const [channelMargins, setChannelMargins] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = () => localStorage.getItem('hw_token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  useEffect(() => {
    loadCampaigns();
    loadTemplates();
    loadVideos();
  }, []);

  const loadCampaigns = async () => {
    try {
      const r = await axios.get(`${API}/guest-videos/campaigns`, { headers: authHeaders() });
      setCampaigns(r.data.campaigns || []);
    } catch (e) { toast.error('Errore caricamento campagne'); }
  };
  const loadTemplates = async () => {
    try {
      const r = await axios.get(`${API}/guest-videos/templates`, { headers: authHeaders() });
      setTemplates(r.data.templates || {});
    } catch (e) { toast.error('Errore caricamento template'); }
  };
  const loadVideos = async () => {
    try {
      const r = await axios.get(`${API}/guest-videos/videos`, { headers: authHeaders() });
      setVideos(r.data.videos || {});
    } catch (e) { /* silent */ }
  };
  const loadSent = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/guest-videos/sent?days=30&limit=100`, { headers: authHeaders() });
      setSentItems(r.data.items || []);
    } catch (e) { toast.error('Errore caricamento invii'); }
    setLoading(false);
  };
  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [o, ab, bl, cm] = await Promise.all([
        axios.get(`${API}/guest-videos/kpi/overview?days=90`, { headers: authHeaders() }),
        axios.get(`${API}/guest-videos/kpi/ab-comparison?days=90`, { headers: authHeaders() }),
        axios.get(`${API}/guest-videos/kpi/by-language?days=90`, { headers: authHeaders() }),
        axios.get(`${API}/guest-videos/kpi/channel-margins`, { headers: authHeaders() }),
      ]);
      setOverview(o.data);
      setAbComparison(ab.data);
      setByLang(bl.data);
      setChannelMargins(cm.data.channels || []);
    } catch (e) { toast.error('Errore caricamento KPI'); }
    setLoading(false);
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl space-y-6" data-testid="guest-video-campaigns-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Video className="h-8 w-8 text-rose-600" />
            Guest Video Engagement
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Sequenza di 4 video WhatsApp via Spoki, A/B/C test, tracking conversioni lifelong
          </p>
        </div>
        <a
          href="/leads-crm"
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-600 to-pink-600 text-white text-sm font-semibold rounded-lg shadow hover:shadow-lg transition-shadow"
          data-testid="goto-leads-crm"
        >
          👥 CRM Lead Proprietari & Partner →
        </a>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => {
        setActiveTab(v);
        if (v === 'sent') loadSent();
        if (v === 'analytics') loadAnalytics();
      }}>
        <TabsList className="grid w-full grid-cols-5 max-w-3xl">
          <TabsTrigger value="campaigns" data-testid="tab-campaigns"><Video className="h-4 w-4 mr-1.5" />Campagne</TabsTrigger>
          <TabsTrigger value="videos" data-testid="tab-videos"><Film className="h-4 w-4 mr-1.5" />Video</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates"><Webhook className="h-4 w-4 mr-1.5" />Spoki</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent"><Send className="h-4 w-4 mr-1.5" />Invii</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics"><BarChart3 className="h-4 w-4 mr-1.5" />Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-4 mt-4">
          <CampaignsTab campaigns={campaigns} onUpdate={loadCampaigns} authHeaders={authHeaders} />
        </TabsContent>
        <TabsContent value="videos" className="space-y-4 mt-4">
          <VideosTab videos={videos} onUpdate={loadVideos} authHeaders={authHeaders} />
        </TabsContent>
        <TabsContent value="templates" className="space-y-4 mt-4">
          <TemplatesTab templates={templates} onUpdate={loadTemplates} authHeaders={authHeaders} />
        </TabsContent>
        <TabsContent value="sent" className="space-y-4 mt-4">
          <SentTab items={sentItems} loading={loading} onReload={loadSent} />
        </TabsContent>
        <TabsContent value="analytics" className="space-y-4 mt-4">
          <AnalyticsTab overview={overview} abComparison={abComparison} byLang={byLang} channelMargins={channelMargins} loading={loading} onReload={loadAnalytics} authHeaders={authHeaders} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CampaignsTab({ campaigns, onUpdate, authHeaders }) {
  const [editing, setEditing] = useState({});
  const saveCampaign = async (id, payload) => {
    try {
      await axios.put(`${API}/guest-videos/campaigns/${id}`, payload, { headers: authHeaders() });
      toast.success('Campagna salvata');
      setEditing({...editing, [id]: false});
      onUpdate();
    } catch (e) { toast.error('Errore: ' + (e.response?.data?.detail || e.message)); }
  };
  return (
    <div className="space-y-4">
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <p className="text-sm text-blue-900 flex items-start gap-2">
            <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Come funziona:</strong> ogni ospite viene assegnato deterministicamente a una delle 3 varianti
              A/B/C (sticky cross-booking via email/telefono). I 3 video vengono inviati alle <strong>19:00 Europe/Rome</strong>
              nei giorni previsti dalla variante. Idempotency garantita.
            </span>
          </p>
        </CardContent>
      </Card>

      {campaigns.map(camp => {
        const isEdit = editing[camp.id];
        const formState = editing[`${camp.id}_form`] || camp;
        return (
          <Card key={camp.id} data-testid={`campaign-card-${camp.order}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 text-white flex items-center justify-center font-bold text-lg">
                    V{camp.order}
                  </div>
                  <div>
                    <CardTitle className="text-xl">{camp.name}</CardTitle>
                    <CardDescription className="mt-0.5">{camp.description}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={camp.enabled ? 'default' : 'secondary'} className={camp.enabled ? 'bg-emerald-600' : ''}>
                    {camp.enabled ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                    {camp.enabled ? 'Attiva' : 'Disattiva'}
                  </Badge>
                  {!isEdit ? (
                    <Button size="sm" variant="outline" onClick={() => setEditing({...editing, [camp.id]: true, [`${camp.id}_form`]: {...camp}})}
                      data-testid={`edit-campaign-${camp.order}`}>Modifica</Button>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditing({...editing, [camp.id]: false})}>Annulla</Button>
                      <Button size="sm" onClick={() => saveCampaign(camp.id, formState)} data-testid={`save-campaign-${camp.order}`}>
                        <Save className="h-3.5 w-3.5 mr-1.5" />Salva
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!isEdit ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-slate-500">Spoki type:</span><br/><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{camp.spoki_message_type}</code></div>
                  <div><span className="text-slate-500">Ora invio:</span><br/><span className="font-mono">{camp.send_hour_local}:00 Italia</span></div>
                  <div><span className="text-slate-500">Min notti:</span><br/>{camp.min_nights || 'Nessuno'}</div>
                  <div><span className="text-slate-500">A/B/C:</span><br/>{(camp.ab_variants_active || []).join(', ')}</div>
                  <div className="col-span-2"><span className="text-slate-500">Canali esclusi:</span><br/>{camp.excluded_channels?.length ? camp.excluded_channels.join(', ') : 'Nessuno'}</div>
                  <div className="col-span-2"><span className="text-slate-500">Consenso marketing:</span><br/>{camp.require_marketing_consent ? 'Richiesto' : 'Non richiesto (utente: ok da TOS prenotazione)'}</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome</Label>
                    <Input value={formState.name} onChange={e => setEditing({...editing, [`${camp.id}_form`]: {...formState, name: e.target.value}})} />
                  </div>
                  <div>
                    <Label>Spoki message type</Label>
                    <Input value={formState.spoki_message_type} onChange={e => setEditing({...editing, [`${camp.id}_form`]: {...formState, spoki_message_type: e.target.value}})} />
                  </div>
                  <div>
                    <Label>Ora invio (locale Italia, 0-23)</Label>
                    <Input type="number" min="0" max="23" value={formState.send_hour_local || 19}
                      onChange={e => setEditing({...editing, [`${camp.id}_form`]: {...formState, send_hour_local: parseInt(e.target.value)}})} />
                  </div>
                  <div>
                    <Label>Minimo notti soggiorno</Label>
                    <Input type="number" min="0" value={formState.min_nights || 0}
                      onChange={e => setEditing({...editing, [`${camp.id}_form`]: {...formState, min_nights: parseInt(e.target.value)}})} />
                  </div>
                  <div>
                    <Label>Canali esclusi (virgola: airbnb, booking…)</Label>
                    <Input value={(formState.excluded_channels || []).join(', ')}
                      onChange={e => setEditing({...editing, [`${camp.id}_form`]: {...formState, excluded_channels: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}})} />
                  </div>
                  <div>
                    <Label>Varianti A/B/C attive (virgola)</Label>
                    <Input value={(formState.ab_variants_active || ['A','B','C']).join(', ')}
                      onChange={e => setEditing({...editing, [`${camp.id}_form`]: {...formState, ab_variants_active: e.target.value.split(',').map(s=>s.trim().toUpperCase()).filter(Boolean)}})} />
                  </div>
                  <div className="md:col-span-2 flex items-center gap-3">
                    <Switch checked={formState.enabled} onCheckedChange={(v) => setEditing({...editing, [`${camp.id}_form`]: {...formState, enabled: v}})} />
                    <Label>Campagna attiva</Label>
                    <div className="flex-1"></div>
                    <Switch checked={formState.require_marketing_consent} onCheckedChange={(v) => setEditing({...editing, [`${camp.id}_form`]: {...formState, require_marketing_consent: v}})} />
                    <Label>Richiedi consenso marketing</Label>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function TemplatesTab({ templates, onUpdate, authHeaders }) {
  const [showForm, setShowForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ message_type: 'guest_video_1', language: 'it', webhook_url: '', secret: '' });
  const [test, setTest] = useState({ message_type: 'guest_video_1', language: 'it', phone: '', first_name: 'Test' });
  const [testing, setTesting] = useState(false);
  const messageTypes = ['guest_video_1', 'guest_video_2', 'guest_video_3', 'guest_video_4'];
  const saveTemplate = async () => {
    if (!newTemplate.webhook_url) { toast.error('Webhook URL obbligatorio'); return; }
    try {
      await axios.put(`${API}/guest-videos/templates`, newTemplate, { headers: authHeaders() });
      toast.success('Template salvato');
      setShowForm(false);
      setNewTemplate({ message_type: 'guest_video_1', language: 'it', webhook_url: '', secret: '' });
      onUpdate();
    } catch (e) { toast.error('Errore: ' + (e.response?.data?.detail || e.message)); }
  };
  const sendTest = async () => {
    if (!test.phone) { toast.error('Inserisci il numero (formato +39...)'); return; }
    setTesting(true);
    try {
      const r = await axios.post(`${API}/guest-videos/test-send`, test, { headers: authHeaders() });
      if (r.data?.success) toast.success(`Test inviato a ${r.data.phone} (${r.data.language})`);
      else toast.error('Invio fallito: ' + (r.data?.error || 'errore sconosciuto'));
    } catch (e) { toast.error('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setTesting(false); }
  };
  const deleteTemplate = async (message_type, language) => {
    if (!window.confirm(`Eliminare template ${message_type}/${language}?`)) return;
    try {
      await axios.delete(`${API}/guest-videos/templates?message_type=${message_type}&language=${language}`, { headers: authHeaders() });
      toast.success('Template eliminato');
      onUpdate();
    } catch (e) { toast.error('Errore: ' + (e.response?.data?.detail || e.message)); }
  };
  
  const completionMatrix = messageTypes.map(mt => ({
    type: mt,
    languages: LANGUAGES.map(l => ({
      ...l,
      configured: !!(templates[mt]?.[l.code])
    }))
  }));
  
  return (
    <div className="space-y-4">
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <p className="text-sm text-amber-900 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Setup richiesto su Spoki:</strong> per ogni video (V1/V2/V3/V4) e per ogni lingua, crea su Spoki un
              template con trigger a <strong>webhook che invia SUBITO</strong> (NON un'automazione con ritardo: la tempistica
              A/B/C la gestisce HWsmart). Poi inserisci qui sotto l'URL webhook + secret per ogni combinazione
              (32 totali = 4 video × 8 lingue).
              <br/>
              <span className="text-xs">Se manca una lingua, il sistema usa fallback IT (o EN). Per V3/V4 il pulsante del template dev'essere un URL dinamico col token (es. https://hwsmart.it/v/&#123;&#123;token&#125;&#125;).</span>
            </span>
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {completionMatrix.map(mt => (
          <Card key={mt.type}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{mt.type}</CardTitle>
              <CardDescription className="text-xs">{mt.languages.filter(l=>l.configured).length}/8 lingue configurate</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-4 gap-1.5">
                {mt.languages.map(l => (
                  <div key={l.code} className={`flex items-center justify-center gap-0.5 text-xs px-1.5 py-1 rounded border ${l.configured ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`} title={l.label}>
                    <span>{l.flag}</span>
                    <span className="uppercase">{l.code}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setShowForm(!showForm)} data-testid="add-template-btn">
          {showForm ? 'Annulla' : '+ Aggiungi/Modifica Template'}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nuovo Template</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Video (message_type)</Label>
                <select className="w-full border rounded-md px-3 py-2" value={newTemplate.message_type}
                  onChange={e => setNewTemplate({...newTemplate, message_type: e.target.value})}>
                  {messageTypes.map(mt => <option key={mt} value={mt}>{mt}</option>)}
                </select>
              </div>
              <div>
                <Label>Lingua</Label>
                <select className="w-full border rounded-md px-3 py-2" value={newTemplate.language}
                  onChange={e => setNewTemplate({...newTemplate, language: e.target.value})}>
                  {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Webhook URL Spoki</Label>
              <Input placeholder="https://app.spoki.it/api/automation/xxxxx/webhook" value={newTemplate.webhook_url}
                onChange={e => setNewTemplate({...newTemplate, webhook_url: e.target.value})} data-testid="template-webhook-url"/>
            </div>
            <div>
              <Label>Secret (opzionale, default da .env)</Label>
              <Input value={newTemplate.secret} onChange={e => setNewTemplate({...newTemplate, secret: e.target.value})} />
            </div>
            <Button onClick={saveTemplate} data-testid="save-template-btn"><Save className="h-4 w-4 mr-1" />Salva</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Template configurati</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Video</TableHead><TableHead>Lingua</TableHead><TableHead>Webhook URL</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
            <TableBody>
              {Object.entries(templates).flatMap(([mt, byLang]) =>
                Object.entries(byLang || {}).map(([lang, cfg]) => (
                  <TableRow key={`${mt}-${lang}`}>
                    <TableCell><Badge variant="outline">{mt}</Badge></TableCell>
                    <TableCell>{LANGUAGES.find(l=>l.code===lang)?.flag} {lang.toUpperCase()}</TableCell>
                    <TableCell className="font-mono text-xs truncate max-w-md">{cfg.webhook_url}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => deleteTemplate(mt, lang)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {Object.keys(templates).length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">Nessun template configurato</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card data-testid="test-send-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">🧪 Invia test</CardTitle>
          <CardDescription className="text-xs">Invia un video al tuo numero per validare template + webhook. NON tocca ospiti reali né le statistiche.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label>Video</Label>
              <select className="w-full border rounded-md px-3 py-2" value={test.message_type}
                onChange={e => setTest({...test, message_type: e.target.value})} data-testid="test-message-type">
                {messageTypes.map(mt => <option key={mt} value={mt}>{mt}</option>)}
              </select>
            </div>
            <div>
              <Label>Lingua</Label>
              <select className="w-full border rounded-md px-3 py-2" value={test.language}
                onChange={e => setTest({...test, language: e.target.value})} data-testid="test-language">
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Numero (+39...)</Label>
              <Input placeholder="+39 333 1234567" value={test.phone}
                onChange={e => setTest({...test, phone: e.target.value})} data-testid="test-phone"/>
            </div>
            <Button onClick={sendTest} disabled={testing} data-testid="send-test-btn">
              {testing ? 'Invio...' : 'Invia test'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SentTab({ items, loading, onReload }) {
  const statusColor = {
    sent: 'bg-blue-100 text-blue-800',
    delivered: 'bg-cyan-100 text-cyan-800',
    read: 'bg-emerald-100 text-emerald-800',
    clicked: 'bg-purple-100 text-purple-800',
    failed: 'bg-red-100 text-red-800',
    pending: 'bg-amber-100 text-amber-800',
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Ultimi 30 giorni — {items.length} invii</CardTitle>
          <CardDescription>Storico completo di tutte le sequenze inviate</CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={onReload} disabled={loading} data-testid="reload-sent-btn">
          {loading ? <Clock className="h-4 w-4 animate-spin" /> : 'Ricarica'}
        </Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Data invio</TableHead>
            <TableHead>Booking</TableHead>
            <TableHead>Ospite</TableHead>
            <TableHead>Video</TableHead>
            <TableHead>Variante</TableHead>
            <TableHead>Lingua</TableHead>
            <TableHead>Telefono</TableHead>
            <TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.id}>
                <TableCell className="text-xs font-mono">{(item.sent_at || item.scheduled_at || '').substring(0,16).replace('T',' ')}</TableCell>
                <TableCell className="font-mono text-xs">{item.booking_code}</TableCell>
                <TableCell className="text-sm">{item.guest_first_name} {item.guest_last_name?.[0]}.</TableCell>
                <TableCell><Badge variant="outline">V{item.campaign_order}</Badge></TableCell>
                <TableCell><Badge variant="secondary">{item.variant}</Badge></TableCell>
                <TableCell>{LANGUAGES.find(l=>l.code===item.language)?.flag || '?'} {item.language?.toUpperCase()}</TableCell>
                <TableCell className="font-mono text-xs">{item.guest_phone_e164?.slice(0,6)}***{item.guest_phone_e164?.slice(-3)}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded ${statusColor[item.status] || 'bg-slate-100 text-slate-700'}`}>{item.status}</span></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-12">Nessun invio negli ultimi 30 giorni</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function AnalyticsTab({ overview, abComparison, byLang, channelMargins, loading, onReload, authHeaders }) {
  useEffect(() => { if (!overview) onReload(); /* eslint-disable-next-line */ }, []);
  const recomputeKPI = async () => {
    try {
      await axios.post(`${API}/guest-videos/kpi/recompute`, {}, { headers: authHeaders() });
      toast.success('Ricalcolo KPI avviato (può richiedere 1-2 minuti)');
      setTimeout(onReload, 5000);
    } catch (e) { toast.error('Errore ricalcolo'); }
  };
  const backfillConv = async () => {
    try {
      const r = await axios.post(`${API}/guest-videos/backfill-conversions?limit=2000`, {}, { headers: authHeaders() });
      toast.success(`Backfill: ${r.data.registered} conversioni registrate su ${r.data.scanned} booking`);
      setTimeout(onReload, 2000);
    } catch (e) { toast.error('Errore backfill'); }
  };
  if (loading && !overview) return <div className="text-center py-12 text-slate-400"><Clock className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento KPI…</div>;
  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={backfillConv} data-testid="backfill-conv-btn">
          <TrendingUp className="h-3.5 w-3.5 mr-1.5" />Backfill conversioni storiche
        </Button>
        <Button size="sm" variant="outline" onClick={recomputeKPI} data-testid="recompute-kpi-btn">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" />Ricalcola margini canale
        </Button>
        <Button size="sm" onClick={onReload} disabled={loading} data-testid="reload-analytics-btn">
          {loading ? <Clock className="h-4 w-4 animate-spin" /> : 'Aggiorna'}
        </Button>
      </div>

      {/* Top-line KPI */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label="Inviati (90gg)" value={overview.funnel?.sent || 0} icon={Send} color="from-blue-500 to-blue-600" />
          <KpiCard label="Consegnati" value={overview.funnel?.delivered || 0} icon={CheckCircle2} color="from-cyan-500 to-cyan-600" subValue={`${(overview.funnel.sent ? overview.funnel.delivered/overview.funnel.sent*100 : 0).toFixed(0)}%`} />
          <KpiCard label="Letti" value={overview.funnel?.read || 0} icon={CheckCircle2} color="from-emerald-500 to-emerald-600" />
          <KpiCard label="Direct Rebook" value={overview.conversions?.direct_rebook?.count || 0} icon={Trophy} color="from-amber-500 to-amber-600" subValue={`€${(overview.conversions?.direct_rebook?.revenue_eur || 0).toLocaleString('it')}`} />
          <KpiCard label="Avg Owner/anno" value={`€${(overview.current_year_kpis?.avg_property_owner_revenue_eur || 0).toLocaleString('it')}`} icon={Euro} color="from-rose-500 to-pink-600" />
        </div>
      )}

      {/* A/B/C Comparison */}
      {abComparison && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4" />Confronto A/B/C (90gg)</CardTitle>
            <CardDescription>
              {abComparison.winner_by_conversion_rate ? <>🏆 Variante <strong>{abComparison.winner_by_conversion_rate}</strong> vince per conversioni</> : 'Ancora pochi dati per dichiarare un vincitore'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {abComparison.variants?.map(v => (
                <div key={v.variant} className={`rounded-lg border-2 p-4 ${VARIANT_INFO[v.variant]?.color || 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="text-2xl font-bold">Variante {v.variant}</h3>
                    {v.variant === abComparison.winner_by_conversion_rate && <Badge className="bg-amber-500"><Trophy className="h-3 w-3 mr-1" />Top</Badge>}
                  </div>
                  <p className="text-xs opacity-80 mb-3">{VARIANT_INFO[v.variant]?.desc}</p>
                  <div className="space-y-1.5 text-sm">
                    <Row label="V1 inviati" value={v.v1_sent} />
                    <Row label="V1 letti" value={`${v.v1_read} (${v.read_rate_pct}%)`} />
                    <Row label="V2 inviati" value={v.v2_sent} />
                    <Row label="V2 drop-off" value={`${v.v2_dropoff_pct}%`} highlight={v.v2_dropoff_pct > 15} />
                    <Row label="V3 inviati" value={v.v3_sent} />
                    <Row label="V3 drop-off" value={`${v.v3_dropoff_pct}%`} highlight={v.v3_dropoff_pct > 15} />
                    <div className="border-t pt-2 mt-2">
                      <Row label="🎯 Direct rebook" value={v.direct_rebook_count} highlight={v.direct_rebook_count > 0} />
                      <Row label="💶 Revenue" value={`€${v.direct_rebook_revenue_eur.toLocaleString('it')}`} highlight={v.direct_rebook_revenue_eur > 0} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per lingua */}
      {byLang?.languages?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Languages className="h-4 w-4" />Performance per lingua</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Lingua</TableHead><TableHead className="text-right">Inviati</TableHead><TableHead className="text-right">Consegnati</TableHead><TableHead className="text-right">Letti</TableHead>
                <TableHead className="text-right">Delivery%</TableHead><TableHead className="text-right">Read%</TableHead><TableHead className="text-right">Direct rebook</TableHead><TableHead className="text-right">Revenue</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {byLang.languages.map(l => (
                  <TableRow key={l.language}>
                    <TableCell>{LANGUAGES.find(x=>x.code===l.language)?.flag} {l.language.toUpperCase()}</TableCell>
                    <TableCell className="text-right font-mono">{l.sent}</TableCell>
                    <TableCell className="text-right font-mono">{l.delivered}</TableCell>
                    <TableCell className="text-right font-mono">{l.read}</TableCell>
                    <TableCell className="text-right">{l.delivery_rate_pct}%</TableCell>
                    <TableCell className="text-right">{l.read_rate_pct}%</TableCell>
                    <TableCell className="text-right">{l.direct_rebook?.count || 0}</TableCell>
                    <TableCell className="text-right">€{(l.direct_rebook?.revenue || 0).toLocaleString('it')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Channel margins */}
      {channelMargins.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Euro className="h-4 w-4" />Margini per canale ({new Date().getFullYear()})</CardTitle>
            <CardDescription>Calcolato dinamicamente dai tuoi dati di prenotazione. Si cristallizza il 1° gennaio dell'anno successivo.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Canale</TableHead><TableHead className="text-right">Prenotazioni</TableHead><TableHead className="text-right">Notti</TableHead>
                <TableHead className="text-right">€ lordo/notte</TableHead><TableHead className="text-right">Comm%</TableHead><TableHead className="text-right">€ netto/notte</TableHead>
                <TableHead className="text-right">Shift vs Direct</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {channelMargins.map(c => (
                  <TableRow key={c.channel} className={c.channel === 'direct' ? 'bg-emerald-50' : ''}>
                    <TableCell className="font-semibold">{c.channel}</TableCell>
                    <TableCell className="text-right">{c.bookings_count}</TableCell>
                    <TableCell className="text-right">{c.total_nights}</TableCell>
                    <TableCell className="text-right">€{c.avg_gross_per_night.toFixed(2)}</TableCell>
                    <TableCell className="text-right">{c.commission_pct}%</TableCell>
                    <TableCell className="text-right font-semibold">€{c.avg_net_per_night.toFixed(2)}</TableCell>
                    <TableCell className="text-right">{c.shift_value_eur_per_night > 0 ? `+€${c.shift_value_eur_per_night.toFixed(2)} (${c.shift_value_pct.toFixed(1)}%)` : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color, subValue }) {
  return (
    <Card>
      <CardContent className="pt-4">
        <div className={`bg-gradient-to-br ${color} text-white rounded-md p-2 w-9 h-9 flex items-center justify-center mb-2`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
        {subValue && <div className="text-xs text-slate-400">{subValue}</div>}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between">
      <span className="opacity-70">{label}</span>
      <span className={`font-mono ${highlight ? 'font-bold' : ''}`}>{value}</span>
    </div>
  );
}

function VideosTab({ videos, onUpdate, authHeaders }) {
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});  // {key: {phase, percent, message}}
  const [optimizing, setOptimizing] = useState({});
  const [preparingWa, setPreparingWa] = useState({});
  const [previewLanding, setPreviewLanding] = useState(null);
  const fileInputs = useRef({});
  
  // CHUNKED UPLOAD: 8MB per chunk per bypassare il limite proxy 100MB
  const CHUNK_SIZE = 8 * 1024 * 1024;
  
  const handleUpload = async (campaign_order, language, file) => {
    if (!file) return;
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > 500) {
      toast.error(`File troppo grande (${sizeMB.toFixed(1)}MB, max 500MB)`);
      return;
    }
    const key = `${campaign_order}_${language}`;
    setUploading({ ...uploading, [key]: true });
    setUploadProgress({ ...uploadProgress, [key]: { phase: 'init', percent: 0, message: 'Preparazione…' } });
    
    console.log(`[upload V${campaign_order}/${language}] START — ${sizeMB.toFixed(1)}MB file`);
    
    try {
      // 1) Init session
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const initFd = new FormData();
      initFd.append('campaign_order', campaign_order);
      initFd.append('language', language);
      initFd.append('total_size', file.size);
      initFd.append('total_chunks', totalChunks);
      initFd.append('filename', file.name);
      console.log(`[upload] Step 1: INIT (${totalChunks} chunks × ${(CHUNK_SIZE/1024/1024).toFixed(1)}MB)`);
      const initRes = await axios.post(`${API}/guest-videos/videos/chunked-upload/init`, initFd, {
        headers: { ...authHeaders() },
        timeout: 30000,
      });
      const uploadId = initRes.data.upload_id;
      console.log(`[upload] ✅ INIT OK, upload_id=${uploadId}`);
      
      // 2) Upload chunks in sequence
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkBlob = file.slice(start, end);
        const chunkFd = new FormData();
        chunkFd.append('upload_id', uploadId);
        chunkFd.append('chunk_index', i);
        chunkFd.append('chunk', chunkBlob, `chunk_${i}`);
        console.log(`[upload] Step 2.${i+1}: chunk ${i+1}/${totalChunks} (${(chunkBlob.size/1024/1024).toFixed(1)}MB)…`);
        try {
          await axios.post(`${API}/guest-videos/videos/chunked-upload/chunk`, chunkFd, {
            headers: { ...authHeaders() },
            timeout: 120000,
          });
        } catch (chunkErr) {
          console.error(`[upload] ❌ Chunk ${i+1} FAILED:`, chunkErr.response?.status, chunkErr.response?.data, chunkErr.message);
          throw new Error(`Chunk ${i+1}/${totalChunks} fallito: ${chunkErr.response?.data?.detail || chunkErr.message}`);
        }
        const percent = Math.round(((i + 1) / totalChunks) * 80);
        setUploadProgress(prev => ({
          ...prev,
          [key]: { phase: 'uploading', percent, message: `Upload ${i + 1}/${totalChunks} (${percent}%)` },
        }));
      }
      console.log(`[upload] ✅ All ${totalChunks} chunks uploaded`);
      
      // 3) Complete: avvia processing async
      setUploadProgress(prev => ({
        ...prev,
        [key]: { phase: 'processing', percent: 85, message: '⚡ Compressione in corso…' },
      }));
      const completeFd = new FormData();
      completeFd.append('upload_id', uploadId);
      completeFd.append('compression_preset', 'balanced');
      console.log(`[upload] Step 3: COMPLETE (avvia compressione async)`);
      await axios.post(`${API}/guest-videos/videos/chunked-upload/complete`, completeFd, {
        headers: { ...authHeaders() },
        timeout: 60000,
      });
      console.log(`[upload] ✅ COMPLETE accettato, polling status…`);
      
      // 4) Poll status every 3s until completed or error (max 10 min)
      const startTime = Date.now();
      const maxWaitMs = 10 * 60 * 1000;
      let result = null;
      while (Date.now() - startTime < maxWaitMs) {
        await new Promise(res => setTimeout(res, 3000));
        try {
          const statusRes = await axios.get(
            `${API}/guest-videos/videos/chunked-upload/status/${uploadId}`,
            { headers: authHeaders(), timeout: 15000 }
          );
          const elapsed = Math.round((Date.now() - startTime) / 1000);
          setUploadProgress(prev => ({
            ...prev,
            [key]: { phase: 'processing', percent: 90, message: `⚡ Compressione… (${elapsed}s)` },
          }));
          if (statusRes.data.status === 'completed') {
            result = statusRes.data.result;
            break;
          }
          if (statusRes.data.status === 'error') {
            throw new Error(statusRes.data.error || 'Errore lato server');
          }
        } catch (e) {
          if (e.response?.status === 404) throw e;
          // continua a pollare
        }
      }
      if (!result) throw new Error('Timeout compressione (>10min)');
      
      setUploadProgress(prev => ({
        ...prev,
        [key]: { phase: 'done', percent: 100, message: 'Fatto!' },
      }));
      
      const msg = result.compressed
        ? `✅ V${campaign_order} ${language.toUpperCase()}: ${result.size_before_mb}→${result.size_mb}MB (-${result.savings_pct}%)`
        : `✅ V${campaign_order} ${language.toUpperCase()} caricato (${result.size_mb}MB, già ottimizzato)`;
      toast.success(msg);
      onUpdate();
    } catch (e) {
      toast.error('Upload fallito: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading({ ...uploading, [key]: false });
      // Cleanup progresso dopo 3 sec
      setTimeout(() => {
        setUploadProgress(prev => {
          const np = { ...prev };
          delete np[key];
          return np;
        });
      }, 3000);
    }
  };
  
  const handleOptimize = async (campaign_order, language) => {
    const key = `${campaign_order}_${language}`;
    setOptimizing({ ...optimizing, [key]: true });
    try {
      // 1) Avvia job async
      const startRes = await axios.post(
        `${API}/guest-videos/videos/${campaign_order}/${language}/optimize`,
        {},
        { headers: authHeaders(), timeout: 30000 }
      );
      
      // Skip immediato (già ottimizzato)
      if (startRes.data.skipped) {
        toast.info(`V${campaign_order} ${language.toUpperCase()}: ${startRes.data.reason}`);
        return startRes.data;
      }
      
      const jobId = startRes.data.job_id;
      console.log(`[optimize] V${campaign_order}/${language} job_id=${jobId}, polling…`);
      
      // 2) Polling status (max 10 min)
      const startTime = Date.now();
      const maxWaitMs = 10 * 60 * 1000;
      let final = null;
      while (Date.now() - startTime < maxWaitMs) {
        await new Promise(res => setTimeout(res, 3000));
        try {
          const st = await axios.get(
            `${API}/guest-videos/videos/optimize/status/${jobId}`,
            { headers: authHeaders(), timeout: 15000 }
          );
          if (st.data.status === 'completed') {
            final = st.data.result;
            break;
          }
          if (st.data.status === 'error') {
            throw new Error(st.data.error || 'Errore lato server');
          }
        } catch (e) {
          if (e.response?.status === 404) throw e;
          // ignora errori intermittenti polling
        }
      }
      if (!final) throw new Error('Timeout compressione (>10min)');
      
      if (final.skipped) {
        toast.info(`V${campaign_order} ${language.toUpperCase()}: ${final.reason}`);
      } else {
        toast.success(`✅ V${campaign_order} ${language.toUpperCase()}: ${final.size_before_mb}→${final.size_after_mb}MB (-${final.savings_pct}%)`);
      }
      onUpdate();
      return final;
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`V${campaign_order} ${language.toUpperCase()}: ${msg}`);
      throw e;
    } finally {
      setOptimizing(prev => ({ ...prev, [key]: false }));
    }
  };
  
  // Scarica una copia compressa ≤16MB pronta per Spoki/WhatsApp (prepara async + download)
  const handleDownloadWhatsApp = async (campaign_order, language) => {
    const key = `${campaign_order}_${language}`;
    setPreparingWa(prev => ({ ...prev, [key]: true }));
    try {
      const startRes = await axios.post(
        `${API}/guest-videos/videos/${campaign_order}/${language}/prepare-whatsapp`,
        {},
        { headers: authHeaders(), timeout: 30000 }
      );
      const jobId = startRes.data.job_id;
      // Polling status (max 10 min) — riusa l'endpoint optimize/status
      const startTime = Date.now();
      const maxWaitMs = 10 * 60 * 1000;
      let final = null;
      while (Date.now() - startTime < maxWaitMs) {
        await new Promise(res => setTimeout(res, 3000));
        try {
          const st = await axios.get(
            `${API}/guest-videos/videos/optimize/status/${jobId}`,
            { headers: authHeaders(), timeout: 15000 }
          );
          if (st.data.status === 'completed') { final = st.data.result; break; }
          if (st.data.status === 'error') throw new Error(st.data.error || 'Errore lato server');
        } catch (e) {
          if (e.response?.status === 404) throw e;
        }
      }
      if (!final) throw new Error('Timeout preparazione (>10min)');
      // Scarica il file (blob) e forza il download nel browser
      const blobRes = await axios.get(
        `${API}/guest-videos/videos/${campaign_order}/${language}/download-whatsapp`,
        { headers: authHeaders(), responseType: 'blob', timeout: 120000 }
      );
      const url = window.URL.createObjectURL(new Blob([blobRes.data], { type: 'video/mp4' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `HW_V${campaign_order}_${language.toUpperCase()}_whatsapp.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const note = final.over_target ? ' (⚠️ leggermente sopra 16MB, valuta un video più corto)' : '';
      toast.success(`✅ V${campaign_order} ${language.toUpperCase()}: scaricato ${final.size_mb}MB per Spoki${note}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`V${campaign_order} ${language.toUpperCase()}: ${msg}`);
    } finally {
      setPreparingWa(prev => ({ ...prev, [key]: false }));
    }
  };
  
  const handleOptimizeAll = async () => {
    const toOptimize = [];
    Object.entries(videos || {}).forEach(([order, langs]) => {
      Object.entries(langs).forEach(([lang, meta]) => {
        if (!meta.compression_preset || meta.compression_preset === 'none') {
          toOptimize.push({ order: parseInt(order), lang });
        }
      });
    });
    if (toOptimize.length === 0) {
      toast.info('Tutti i video sono già ottimizzati ✅');
      return;
    }
    if (!window.confirm(`Ottimizzare ${toOptimize.length} video? Tempo stimato ~${toOptimize.length} minuti. Puoi continuare a usare l'app.`)) return;
    
    toast.info(`🚀 Avvio ottimizzazione di ${toOptimize.length} video in sequenza…`);
    let done = 0;
    let errors = 0;
    const failedItems = [];
    
    for (let i = 0; i < toOptimize.length; i++) {
      const v = toOptimize[i];
      const label = `V${v.order} ${v.lang.toUpperCase()} (${i + 1}/${toOptimize.length})`;
      console.log(`[optimize-all] ▶ ${label}`);
      try {
        const result = await handleOptimize(v.order, v.lang);
        if (result && !result.skipped) done++;
      } catch (e) {
        errors++;
        const msg = e.response?.data?.detail || e.message;
        failedItems.push(`${label}: ${msg}`);
        console.error(`[optimize-all] ❌ ${label}: ${msg}`);
        // CONTINUA con il prossimo, non interrompere
      }
    }
    
    onUpdate();
    if (errors === 0) {
      toast.success(`✅ Ottimizzati tutti i ${done} video!`);
    } else {
      toast.warning(`Ottimizzati ${done}/${toOptimize.length}. ${errors} errori — clicca ⚡ su ciascun video mancante per riprovare.`, { duration: 15000 });
    }
  };
  
  const handleDelete = async (campaign_order, language) => {
    if (!window.confirm(`Eliminare video V${campaign_order} ${language.toUpperCase()}?`)) return;
    try {
      await axios.delete(`${API}/guest-videos/videos?campaign_order=${campaign_order}&language=${language}`, { headers: authHeaders() });
      toast.success('Video eliminato');
      onUpdate();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };
  
  const generatePreviewLink = async (campaign_order, language) => {
    try {
      const r = await axios.post(
        `${API}/guest-videos/landing-url-preview?campaign_order=${campaign_order}&language=${language}`,
        {},
        { headers: authHeaders() }
      );
      setPreviewLanding({ url: r.data.landing_url, campaign_order, language });
    } catch (e) {
      toast.error('Errore generazione anteprima');
    }
  };
  
  const campaignVideoLabels = { 1: 'V1 Benvenuto', 2: 'V2 Direct Booking', 3: 'V3 Affidaci Immobile', 4: 'V4 Diventa Partner' };
  
  return (
    <div className="space-y-4">
      <Card className="bg-emerald-50 border-emerald-200">
        <CardContent className="pt-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <p className="text-sm text-emerald-900 flex items-start gap-2 flex-1">
              <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Hosting brandizzato HW:</strong> i video vengono caricati su Google Cloud Storage e mostrati su una
                landing page del tuo dominio <code className="bg-emerald-100 px-1 rounded">hwsmart.it/v/&#123;token&#125;</code>.
                <br/>📦 <strong>Upload chunked + compressione automatica</strong>: puoi caricare file fino a 500MB, vengono auto-ottimizzati a ~25MB per uno streaming fluido anche su 4G/iPad.
              </span>
            </p>
            <Button
              size="sm"
              variant="outline"
              className="bg-white hover:bg-amber-50 border-amber-300 text-amber-800 whitespace-nowrap"
              onClick={handleOptimizeAll}
              disabled={Object.values(optimizing).some(Boolean)}
              data-testid="optimize-all-videos-btn"
            >
              ⚡ Ottimizza video esistenti
            </Button>
          </div>
        </CardContent>
      </Card>

      {previewLanding && (
        <Card className="border-rose-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ExternalLink className="h-4 w-4" /> Anteprima landing — V{previewLanding.campaign_order} {previewLanding.language.toUpperCase()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              <a href={previewLanding.url} target="_blank" rel="noopener noreferrer"
                className="text-xs font-mono bg-slate-100 px-2 py-1.5 rounded break-all hover:bg-slate-200 transition">
                {previewLanding.url}
              </a>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => window.open(previewLanding.url, '_blank')} data-testid="open-preview-landing-btn">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />Apri anteprima
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(previewLanding.url); toast.success('Link copiato'); }}>
                  Copia link
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setPreviewLanding(null)}>Chiudi</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {[1, 2, 3, 4].map(order => (
        <Card key={order} data-testid={`videos-row-${order}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 text-white flex items-center justify-center font-bold text-sm">V{order}</div>
              {campaignVideoLabels[order]}
              <span className="text-xs text-slate-500 ml-2">
                {LANGUAGES.filter(l => videos[order]?.[l.code]).length}/8 lingue caricate
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {LANGUAGES.map(lang => {
                const v = videos[order]?.[lang.code];
                const uploadKey = `${order}_${lang.code}`;
                const isUploading = uploading[uploadKey];
                const isOptimizing = optimizing[uploadKey];
                const isPreparingWa = preparingWa[uploadKey];
                const progress = uploadProgress[uploadKey];
                const isAlreadyOptimized = v && v.compression_preset && v.compression_preset !== 'none';
                return (
                  <div key={lang.code} className={`rounded-lg border ${v ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-slate-50/50'} p-3`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1 text-sm font-medium">
                        <span>{lang.flag}</span>
                        <span className="uppercase">{lang.code}</span>
                      </div>
                      {v ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <span className="text-xs text-slate-400">—</span>}
                    </div>
                    {progress && (
                      <div className="mb-2">
                        <div className="text-[10px] text-slate-700 mb-1">{progress.message}</div>
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-rose-500 to-pink-500 transition-all duration-300" style={{ width: `${progress.percent}%` }} />
                        </div>
                      </div>
                    )}
                    {v ? (
                      <>
                        <video
                          src={v.url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${v.url}` : v.url}
                          controls
                          playsInline
                          webkit-playsinline="true"
                          preload="none"
                          poster=""
                          className="w-full aspect-[9/16] rounded bg-black mb-2"
                          data-testid={`video-preview-${order}-${lang.code}`}
                        />
                        <div className="text-xs text-slate-500 mb-2 flex items-center gap-1 flex-wrap">
                          <span>{v.size_mb}MB</span>
                          {isAlreadyOptimized && (
                            <span title={`Originale ${v.size_before_mb}MB → ${v.compression_savings_pct}% in meno (preset ${v.compression_preset})`} className="text-emerald-700 font-semibold">
                              ⚡{v.compression_savings_pct ? `-${v.compression_savings_pct}%` : 'ottimizzato'}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" className="text-xs h-7 px-2" onClick={() => generatePreviewLink(order, lang.code)} data-testid={`preview-landing-${order}-${lang.code}`}>
                            <ExternalLink className="h-3 w-3 mr-1" />Anteprima
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs h-7 px-2 text-emerald-700 hover:text-emerald-900 border-emerald-300" onClick={() => handleDownloadWhatsApp(order, lang.code)} disabled={isPreparingWa || isUploading || isOptimizing} title="Scarica una copia compressa ≤16MB pronta per Spoki/WhatsApp" data-testid={`download-whatsapp-${order}-${lang.code}`}>
                            {isPreparingWa ? <><Clock className="h-3 w-3 mr-1 animate-spin" />Preparo…</> : <><Download className="h-3 w-3 mr-1" />Spoki ≤16MB</>}
                          </Button>
                          {!isAlreadyOptimized && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-700 hover:text-amber-900" onClick={() => handleOptimize(order, lang.code)} disabled={isOptimizing || isUploading} title="Comprimi questo video">
                              {isOptimizing ? <Clock className="h-3 w-3 animate-spin" /> : '⚡'}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => fileInputs.current[uploadKey]?.click()} disabled={isUploading || isOptimizing} title="Ricarica">
                            {isUploading ? <Clock className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:text-red-700" onClick={() => handleDelete(order, lang.code)} disabled={isUploading || isOptimizing}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => fileInputs.current[uploadKey]?.click()} disabled={isUploading} data-testid={`upload-video-${order}-${lang.code}`}>
                        {isUploading ? <><Clock className="h-3.5 w-3.5 animate-spin mr-1" />Upload…</> : <><Upload className="h-3.5 w-3.5 mr-1" />Carica MP4</>}
                      </Button>
                    )}
                    <input
                      ref={el => fileInputs.current[uploadKey] = el}
                      type="file"
                      accept="video/mp4,video/mov,.mp4,.mov,.m4v"
                      className="hidden"
                      onChange={e => handleUpload(order, lang.code, e.target.files[0])}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <p className="text-xs text-blue-900">
            <strong>Suggerimenti tecnici per video WhatsApp/Mobile:</strong>
            <br/>• Formato: <strong>verticale 9:16</strong> (1080×1920) o quadrato 1:1
            <br/>• Durata: 30-60 secondi (sopra 90s gli ospiti chiudono)
            <br/>• Dimensione: meglio se sotto 30MB per caricamenti rapidi
            <br/>• Sottotitoli embeddati nella lingua target (70% degli utenti WhatsApp guarda muto)
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
